import { useRef, useState } from "react";
import * as XLSX from "xlsx";

// HOOKS
import useBulkUploadAssets from "../../../hooks/assets/useBulkUpload.asset";
import useBulkEditAssets from "../../../hooks/assets/useBulkEdit.asset";

// COMPONENTS
import ActionBar from "../actionBar.component";
import Button from "../../buttons/default.button";
import Modal from "../../modals/modal.component";

// UTILS
import parseExcelDate from "../../../utils/conversions/dates/excel.date.conversion";

const ASSET_INPUT_FIELDS = new Set([
  "_id", "id", "codi_portal_user_id", "customer_id", "customer_business_name",
  "contact_id", "account", "name", "created_at", "updated_at", "asset_serial",
  "asset_type", "asset_types", "asset_type_id", "make", "model", "service_tag",
  "touch", "warranty", "warranty_type", "warranty_end_date", "warranty_start_date",
]);

const REQUIRED_FIELDS = [
  { key: "customer_id", label: "Customer ID" },
  { key: "asset_serial", label: "Asset Serial" },
  { key: "asset_type", label: "Asset Type" },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "warranty", label: "Warranty" },
  { key: "warranty_type", label: "Warranty Type" },
];

const validateRows = (rows) => {
  const errors = [];
  rows.forEach((asset, index) => {
    const rowNum = index + 2;
    const ref = asset.asset_serial || `row ${rowNum}`;
    const missing = REQUIRED_FIELDS.filter(({ key }) => {
      const val = asset[key];
      if (key === "warranty") return val === undefined || val === null;
      return val === undefined || val === null || val === "";
    }).map(({ label }) => label);
    if (missing.length > 0) {
      errors.push(`Row ${rowNum} (${ref}): Missing — ${missing.join(", ")}`);
    }
  });
  return errors;
};

const toExcelDateString = (value) => {
  if (typeof value === "number") return parseExcelDate(value).toISOString();
  if (value instanceof Date) return value.toISOString();
  return value;
};

const parseExcelRows = (sheet) => {
  const jsonArray = XLSX.utils.sheet_to_json(sheet);
  const rows = [];

  jsonArray.forEach((i) => {
    if (i["WarrantyStartDate"] > i["WarrantyEndDate"]) return;

    const obj = {};
    for (const key in i) {
      const norm = key.replace(/^﻿/, "").toLowerCase().replace(/\s+/g, "_").trim();
      switch (norm) {
        case "asset_type_id":
        case "asset_type":       obj["asset_type"] = i[key]; break;
        case "customer_id":      obj["customer_id"] = parseInt(i[key]) || null; break;
        case "contact_id":       obj["contact_id"] = parseInt(i[key]) || null; break;
        case "asset_serial":     obj["asset_serial"] = String(i[key]); break;
        case "warrantystartdate":
        case "warranty_start_date": obj["warranty_start_date"] = toExcelDateString(i[key]); break;
        case "warrantyenddate":
        case "warranty_end_date":   obj["warranty_end_date"] = toExcelDateString(i[key]); break;
        case "warranty":         obj["warranty"] = i[key] === "Yes"; break;
        case "warranty_type":    obj["warranty_type"] = i[key]; break;
        case "touch":            obj["touch"] = i[key] === "Yes"; break;
        default:                 obj[norm] = i[key]; break;
      }
    }

    const filtered = {};
    for (const key of Object.keys(obj)) {
      if (ASSET_INPUT_FIELDS.has(key)) filtered[key] = obj[key];
    }
    rows.push(filtered);
  });

  return rows;
};

const AssetActionBar = ({ children }) => {
  const { bulkUploadAssets, loading: isUploading } = useBulkUploadAssets();
  const { bulkEditAssets, loading: isEditing } = useBulkEditAssets();

  const modeRef = useRef(null); // "upload" | "edit"
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [processingState, setProcessingState] = useState(null); // { mode, count }
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null); // { mode, succeeded, failed }

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const mode = modeRef.current;

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = parseExcelRows(sheet);

    document.getElementById("fileInput").value = "";

    if (!rows.length) {
      alert("No data to upload");
      return;
    }

    const validationErrors = validateRows(rows);
    if (validationErrors.length > 0) {
      setUploadErrors(validationErrors);
      setErrorModalOpen(true);
      return;
    }

    const label = mode === "upload" ? "Creating" : "Updating";
    setProcessingState({ mode, count: rows.length });

    try {
      const result = mode === "upload"
        ? await bulkUploadAssets(rows)
        : await bulkEditAssets(rows);

      const succeeded = mode === "upload" ? (result?.created ?? []) : (result?.updated ?? []);
      const failed = result?.failed ?? [];
      setSummaryResult({ mode, succeeded, failed });
      setSummaryModalOpen(true);
    } catch (err) {
      alert(`Error: ${err?.message}`);
    } finally {
      setProcessingState(null);
    }
  };

  const triggerFile = (mode) => {
    modeRef.current = mode;
    document.getElementById("fileInput").click();
  };

  return (
    <>
      {/* Validation error dialog */}
      <Modal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        containerClassName="bg-white rounded-2xl relative top-20 mx-auto w-full max-w-sm shadow-xl flex flex-col overflow-hidden"
      >
        <div className="px-6 pt-10 pb-4 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Issues Found</h2>
          <p className="text-sm text-gray-500 mt-1">
            {uploadErrors.length} issue{uploadErrors.length !== 1 ? "s" : ""} found. Fix before retrying.
          </p>
        </div>
        <details className="group flex flex-col min-h-0 px-6 pb-6">
          <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 select-none list-none flex items-center gap-1">
            <span className="transition-transform group-open:rotate-90">▶</span>
            View details ({uploadErrors.length})
          </summary>
          <ul className="mt-3 flex flex-col gap-2 overflow-y-auto max-h-60 pr-1">
            {uploadErrors.map((err, i) => (
              <li key={i} className="text-sm text-gray-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {err}
              </li>
            ))}
          </ul>
        </details>
      </Modal>

      {/* Summary report dialog */}
      <Modal
        isOpen={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        containerClassName="bg-white rounded-2xl relative top-20 mx-auto w-full max-w-sm shadow-xl flex flex-col overflow-hidden"
      >
        <div className="px-6 pt-8 pb-4 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">
            {summaryResult?.mode === "upload" ? "Upload" : "Edit"} Complete
          </h2>
          <div className="mt-3 flex gap-4">
            <div className="flex-1 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-center">
              <p className="text-2xl font-bold text-green-700">{summaryResult?.succeeded?.length ?? 0}</p>
              <p className="text-xs text-green-600 mt-0.5">
                {summaryResult?.mode === "upload" ? "Created" : "Updated"}
              </p>
            </div>
            <div className="flex-1 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-center">
              <p className="text-2xl font-bold text-red-700">{summaryResult?.failed?.length ?? 0}</p>
              <p className="text-xs text-red-600 mt-0.5">Failed</p>
            </div>
          </div>
        </div>

        {summaryResult?.failed?.length > 0 && (
          <details className="group flex flex-col min-h-0 px-6 pb-6">
            <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 select-none list-none flex items-center gap-1">
              <span className="transition-transform group-open:rotate-90">▶</span>
              Failed records ({summaryResult.failed.length})
            </summary>
            <ul className="mt-3 flex flex-col gap-2 overflow-y-auto max-h-52 pr-1">
              {summaryResult.failed.map((item, i) => (
                <li key={i} className="text-sm text-gray-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <span className="font-medium">{item.asset_serial}</span>
                  <span className="text-gray-500"> — {item.reason}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        <div className="px-6 pb-6 flex-shrink-0">
          <button
            onClick={() => setSummaryModalOpen(false)}
            className="w-full text-sm font-medium bg-gray-900 text-white rounded-xl py-2.5 hover:bg-gray-700 transition-colors"
          >
            Done
          </button>
        </div>
      </Modal>

      {/* Processing loader dialog */}
      <Modal
        isOpen={!!processingState}
        onClose={() => {}}
        containerClassName="bg-white rounded-2xl relative top-20 mx-auto w-full max-w-xs shadow-xl flex flex-col overflow-hidden pointer-events-none"
      >
        <div className="px-8 py-8 flex flex-col items-center gap-4">
          <svg className="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-800">
              {processingState?.mode === "upload" ? "Creating" : "Updating"} {processingState?.count} asset{processingState?.count !== 1 ? "s" : ""}…
            </p>
            <p className="text-xs text-gray-500 mt-1">Please don't close this page.</p>
          </div>
        </div>
      </Modal>

      <ActionBar>
        <input
          id="fileInput"
          type="file"
          accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={handleFileUpload}
        />
        <Button loading={isUploading} handleClick={() => triggerFile("upload")}>
          Bulk Upload
        </Button>
        <Button loading={isEditing} handleClick={() => triggerFile("edit")}>
          Bulk Edit
        </Button>
        <a href="/sample_assets.xlsx" download>
          <Button>Download Sample</Button>
        </a>
        {children}
      </ActionBar>
    </>
  );
};

export default AssetActionBar;
